import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { WINDOW } from 'src/app/core/core.module';
import { RejectContract } from 'src/app/recruiting/candidates/assignments/models/reject-contract.model';
import { CandidateGridItem } from 'src/app/recruiting/candidates/grid/models/candidate-grid-item';
import { CandidateGridResponse } from 'src/app/recruiting/candidates/grid/models/candidate-grid-response';
import { CandidateNotes } from 'src/app/recruiting/candidates/new-profile/models/candidate-notes';
import { MarketingPreferencesEntity } from 'src/app/recruiting/candidates/new-profile/models/marketing-preferences.model';
import { CandidateReferenceCheckDetails } from 'src/app/recruiting/candidates/new-profile/models/references/references.model';
import { TextMessage } from 'src/app/recruiting/candidates/new-profile/models/text-message.model';
import {
    GapOverlapEntry,
    WorkHistory,
    WorkHistoryGapOverlapDetails,
    WorkHistoryInfoType
} from 'src/app/recruiting/candidates/new-profile/models/work-history.model';
import { ContractRequestsGridResponse } from 'src/app/recruiting/contract-requests/grid/models/contract-requests-grid-response';
import { FacilityGridResponse } from 'src/app/recruiting/facilities/models/facility-grid-response';
import { StateLicensureDetails } from 'src/app/recruiting/jobs/recruiting-state-details/models/state-licensure-details.model';
import { StateLicensureFile } from 'src/app/recruiting/jobs/recruiting-state-details/models/state-licensure-file';
import { MarginGridResponse } from 'src/app/recruiting/margins/grid/models/margin-grid-response.model';
import { RelocationState } from 'src/app/recruiting/recruiting-service/relocation-state-lookup';
import { Candidate } from 'src/app/search-all-candidates/models/candidate.model';
import { JobPreferences } from 'src/app/shared/components/job-preferences/job-preferences-model';
import {
    AlternateNameType,
    PersonalProfessionalInfoLookups
} from 'src/app/shared/components/personal-professional-info/models/personal-professional-info-lookups.model';
import {
    CandidateExpertise,
    PersonalProfessionalInfo
} from 'src/app/shared/components/personal-professional-info/models/personal-professional-info.model';
import { Qualifications } from 'src/app/shared/components/qualifications/models/qualifications';
import { QualificationsLookups } from 'src/app/shared/components/qualifications/models/qualificationsLookups';
import { FetchArgs, PagingToken, PagingTokenFixed } from 'src/app/shared/models/core';
import { Expertise } from 'src/app/shared/models/expertise.model';
import { FacilityGridLookups } from 'src/app/shared/models/facility-grid-lookups';
import { FollowupSummary } from 'src/app/shared/models/followup-summary.model';
import { FollowupTime } from 'src/app/shared/models/followup-time.model';
import { FollowupView } from 'src/app/shared/models/followup-view.model';
import { Followup } from 'src/app/shared/models/followup.model';
import { JobGridItem } from 'src/app/shared/models/job-grid-item.model';
import { JobGridMapLocation } from 'src/app/shared/models/job-grid-map-location.model';
import { JobGridResponse } from 'src/app/shared/models/job-grid-response';
import { Lookup } from 'src/app/shared/models/lookup.model';
import { PageResponse } from 'src/app/shared/models/page-response';
import { Profession } from 'src/app/shared/models/profession.model';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { appendQuery, arrayToQuery, combine, formatDate, getQueryString } from 'src/app/shared/utilities/route';
import { TicketGridItem } from 'src/app/travelx/tickets/ticket-grid-item';
import { PersonalProfessionalInfoEditRequest } from '../shared/components/personal-professional-info/models/personal-professional-info-edit-form.model';
import { QualificationsEditForm } from '../shared/components/qualifications/models/qualificationsEditForm.model';
import { DashboardTileFollowups } from '../shared/models/dashboard-tile-followups.model';
import { FollowUpViewBase } from '../shared/models/followup-base-view.model';
import { JobMatchGridItem } from '../shared/models/job-match-grid-item.model';
import { CandidateFollowupsGridItem } from './candidate-followups/models/candidate-followups-grid-item.model';
import { FollowupsStatus } from './candidate-followups/models/followup-status.model';
import { FollowupsDate } from './candidate-followups/models/followups-date.model';
import { WfdJobGridItem } from './candidates/details/models/wfd-job-grid-item.model';
import { CandidateOpenTicketDto } from './candidates/grid/models/candidate-open-ticket';
import {
    AddCandidateEducation,
    CandidateEducation,
    UpdateCandidateEducationDto
} from './candidates/new-profile/models/candidate-education.model';
import { EducationLookupInfo } from './candidates/new-profile/models/education-lookup-info.model';
import { MarginGridForContractRequest } from './contract-requests/grid/models/margin-grid-for-contract-request';
import { MarginGridForContractRequestV2 } from './contract-requests/grid/models/margin-grid-for-contract-request-v2';
import { JobGridSaveSearchDeleteRequestDto } from './jobs/grid/components/models/job-grid-save-search-delete-request-dto';
import { JobGridSaveSearchRequestDto } from './jobs/grid/components/models/job-grid-save-search-request-dto';
import { JobGridSaveSearchesDto } from './jobs/grid/components/models/job-grid-save-searches-dto';
import { LiveCandidateDto } from './jobs/grid/components/models/live-candidate-dto';
import { RecruiterQualifiedCandidate } from './jobs/recruiter-qualified-candidates/models/recruiter-qualified-candidate.model';
export interface ValidationResult {
    name: string;
    isValid?: boolean;
    message?: string;
}

export interface RecruitingFollowUpService {
    getRecruiterFollowupSummary(id: number, startDate: Date, endDate: Date): Observable<FollowupSummary[]>;
    updateRecruiterResources(followup: FollowUpViewBase): Observable<FollowUpViewBase>;
}

@Injectable({ providedIn: 'root' })
export class RecruitingService implements RecruitingFollowUpService {
    private readonly _reloadEvent$ = new Subject<void>();
    private readonly _requestEditMarketingPreferencesEvent$ = new Subject<void>();
    private readonly coreApiPath: string;

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) settings: Settings,
        @Inject(WINDOW) private readonly _window: Window
    ) {
        this.coreApiPath = combine(settings.coreAPIUrl, 'ayanova');
    }

    acsResumeSearch(searchKeywordForUser: string, skip: number, pageSize: number): Observable<any> {
        const url = `${this.coreApiPath}/acs/resumes`;
        const params = new HttpParams()
            .append('keywords', searchKeywordForUser)
            .append('skip', skip)
            .append('pageSize', pageSize);
        return this.http.get<any>(url, { params }).pipe(
            map((response) => {
                if (!isEmpty(response) && response.results && response.results.data) {
                    return { data: response.results.data, total: response.results.total, filtered: 0 };
                } else {
                    return { data: [], total: 0, filtered: 0 };
                }
            })
        );
    }

    acsResumeCandidateInfo(candidateIds: number[]): Observable<any> {
        let url = `${this.coreApiPath}/Candidate/info?`;

        const queryParams = candidateIds
            .reduce((queryParams, currentId) => `${queryParams}candidateIds=${currentId}&`, '')
            .slice(0, -1);

        url = `${url}${queryParams}`;

        return this.http.get<any>(url);
    }

    searchJobs(args: any): Observable<JobGridResponse> {
        let url = `${this.coreApiPath}/jobs`;
        if (args.filter) {
            url += getQueryString(args.filter);
        }
        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }

        return this.getJobGridPaged(args.pageSize, args.skip, url);
    }

    searchRecommendedJobs(
        candidateId: any,
        args: any,
        showRecommended: boolean,
        showInterested: boolean,
        showSubmitted: boolean
    ): Observable<PagingToken<JobMatchGridItem[]>> {
        let url = `${this.coreApiPath}/jobs/candidate/${candidateId}/recommended-jobs`;
        if (args.filter) {
            url += getQueryString(args.filter);
        }
        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }
        url = appendQuery(
            url,
            `recommended=${showRecommended}`,
            `interested=${showInterested}`,
            `submitted=${showSubmitted}`,
            `pageSize=${args.pageSize}`,
            `skip=${args.skip}`
        );
        return this.http.get<PagingToken<JobMatchGridItem[]>>(url);
    }

    searchWfdRecommendedJobs(candidateId: number, args: FetchArgs): Observable<PagingToken<WfdJobGridItem[]>> {
        let url = `${this.coreApiPath}/candidate/${candidateId}/job-matches-wfd`;
        if (args.filter) {
            args.filter = this.encodeFilters(args);
            url += getQueryString(args.filter);
        }
        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }
        url = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
        return this.http.get<PagingToken<WfdJobGridItem[]>>(url);
    }

    /**
     * This encodes filter field(s) that exists.
     *
     * @param args
     * @returns
     */
    encodeFilters(args: FetchArgs): any {
        const filter: any = args?.filter;
        const newFilters: any = { ...args.filter };
        for (const filterName in args?.filter) {
            if (Object.prototype.hasOwnProperty.call(args.filter, filterName)) {
                newFilters[filterName] = this.encodeFilter(filter[filterName]);
            }
        }
        return newFilters;
    }

    decodeFilters(args: FetchArgs): any {
        const filter: any = args?.filter;
        const newFilters: any = { ...args.filter };
        for (const filterName in args?.filter) {
            if (Object.prototype.hasOwnProperty.call(args.filter, filterName)) {
                newFilters[filterName] = this.decodeFilter(filter[filterName]);
            }
        }
        return newFilters;
    }

    deleteWfdRecommendedJobs(candidateId: number, jobIds: Array<number>): Observable<void> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/job-matches-wfd`;
        return this.http.delete<void>(url, {
            body: jobIds
        });
    }

    getJobsLocation(filter: any): Observable<JobGridMapLocation[]> {
        let url = `${this.coreApiPath}/jobs/locations`;
        if (filter) {
            url += getQueryString(filter);
        }
        // only return job locations if latitude/longitude exist
        return this.http.get<JobGridMapLocation[]>(url, { observe: 'response' }).pipe(
            map((resp) => {
                const jobLocations = resp.body;
                return jobLocations.filter((elem) => elem.latitude !== 0 && elem.longitude !== 0);
            })
        );
    }

    getOpenTickets(
        sortField: string,
        sortType: 'asc' | 'desc',
        pageSize: number,
        skip: number,
        filterArgs: any
    ): Observable<PagingToken<TicketGridItem[]>> {
        const params = {
            ...filterArgs,
            sortField,
            sortType,
            pageSize: pageSize ? pageSize.toString() : '25',
            skip: skip ? skip.toString() : '0'
        };

        const url = combine(this.coreApiPath, 'travelx/tickets/search');
        return this.http.get<PagingToken<TicketGridItem[]>>(url, { params });
    }

    getOpenTicketsCount(candidateId): Observable<number> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/open-tickets-count`;

        return this.http.get<number>(url);
    }

    GetCandidateOpenTickets(candidateId): Observable<CandidateOpenTicketDto[]> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/open-tickets`;

        return this.http.get<CandidateOpenTicketDto[]>(url);
    }

    getLiveCandidatesForJobSearch(recruiterId: number): Observable<LiveCandidateDto[]> {
        const url = `${this.coreApiPath}/candidates/live-nurses/${recruiterId}`;
        return this.http.get<LiveCandidateDto[]>(url);
    }

    validateJobSearchName(recruiterId: number, name: string): Observable<boolean> {
        const url = `${this.coreApiPath}/jobs/jobgridsavesearch/nameExists/${recruiterId}`;
        return this.http.get<boolean>(`${url}?name=${encodeURIComponent(name)}`);
    }

    updateRecruiterJobSearch(jobSearchId: number, dataToUpdate: JobGridSaveSearchRequestDto): Observable<void> {
        const url = `${this.coreApiPath}/jobs/jobgridsavesearch/${jobSearchId}`;
        return this.http.put<void>(url, dataToUpdate);
    }

    getRecruiterJobSearches(recruiterId: number): Observable<JobGridSaveSearchesDto[]> {
        const url = `${this.coreApiPath}/jobs/jobgridsavesearch/${recruiterId}`;
        return this.http.get<JobGridSaveSearchesDto[]>(url);
    }
    saveRecruiterJobSearches(dataToAdd: JobGridSaveSearchRequestDto): Observable<void> {
        const url = `${this.coreApiPath}/jobs/jobgridsavesearch`;
        return this.http.post<void>(url, dataToAdd);
    }
    deleteRecruiterJobSearches(items: JobGridSaveSearchesDto[]): Observable<void> {
        const url = `${this.coreApiPath}/jobs/jobgridsavesearch/deleteSearches`;
        const itemsToDelete = items.map((obj) => obj.jobGridSaveSearchId);
        const deleteItemsRequstDto = { jobGridSaveSearchesIds: itemsToDelete } as JobGridSaveSearchDeleteRequestDto;
        return this.http.post<void>(url, deleteItemsRequstDto);
    }

    searchCandidates(args: any): Observable<CandidateGridResponse> {
        const url = `${this.coreApiPath}/candidates`;

        const data = {
            FilterArgs: args.filter,
            SortField: args.sort.field,
            SortType: args.sort.dir === 'asc' ? 0 : 1,
            PageSize: args.pageSize,
            Skip: args.skip
        };

        return this.http.post<CandidateGridResponse>(url, data);
    }

    searchFacilities(args: any): Observable<FacilityGridResponse> {
        let url = `${this.coreApiPath}/facility/grid-items`;

        if (args.filter) {
            url += getQueryString(args.filter);
        }
        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }

        const path = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
        return this.http.get<FacilityGridResponse>(path);
    }

    public getFacilitiesGridLookups(): Observable<FacilityGridLookups> {
        const url = `${this.coreApiPath}/facility/lookups`;
        return this.http.get<FacilityGridLookups>(url);
    }

    searchMargins(args: any): Observable<MarginGridResponse> {
        let url = `${this.coreApiPath}/recruiting/margin-grid-items`;

        if (args.filter) {
            url += getQueryString(args.filter);
        }
        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }

        const path = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
        return this.http.get<MarginGridResponse>(path);
    }

    private completeMarginForContractRequestQuery(url: string, args: FetchArgs): string {
        if (args.filter) {
            url += getQueryString(args.filter);
        }
        if (args.sort?.field?.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }

        return appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
    }

    searchMarginsForContractRequest(args: FetchArgs): Observable<PagingToken<MarginGridForContractRequest[]>> {
        const url = this.completeMarginForContractRequestQuery(`${this.coreApiPath}/recruiting/margins`, args);
        return this.http.get<PagingToken<MarginGridForContractRequest[]>>(url);
    }

    searchMarginsForContractRequestV2(args: FetchArgs): Observable<PagingToken<MarginGridForContractRequestV2[]>> {
        const url = this.completeMarginForContractRequestQuery(`${this.coreApiPath}/recruiting/margins-list`, args);
        return this.http.get<PagingToken<MarginGridForContractRequestV2[]>>(url);
    }

    linkSampleMarginToJob(jobId: number, sampleMarginId: number) {
        const url = `${this.coreApiPath}/Job/${jobId}/SampleMargin/${sampleMarginId}`;
        return this.http.post<any>(url, {});
    }

    verifySampleMarginLinkedToJob(jobId: number, sampleMarginId: number) {
        const url = combine(this.coreApiPath, `/Job/${jobId}/verify-linked/${sampleMarginId}`);
        return this.http.get<boolean>(url);
    }

    quickSearchCandidates(args: any): Observable<PagingToken<CandidateGridItem[]>> {
        let url = combine(this.coreApiPath, 'candidates/quick-search');

        url = this.generateUrl(args, url);

        return this.http.get<PagingToken<CandidateGridItem[]>>(url);
    }

    rapidSearchCandidates(args: FetchArgs): Observable<Candidate[]> {
        let url = combine(this.coreApiPath, 'candidates/rapid-search');

        url = this.generateUrl(args, url);

        return this.http.get<Candidate[]>(url);
    }

    getJobDetailsById(id: number) {
        const url = combine(this.coreApiPath, `jobs/${id}`);

        return this.http.get<any>(url);
    }

    getWeeklyPayRates(jobId: number) {
        const url = combine(this.coreApiPath, `/jobs/${jobId}/weekly-pay-ranges`);

        return this.http.get<any>(url);
    }

    getPerDiemPayRate(jobId: number) {
        const url = combine(this.coreApiPath, `/jobs/${jobId}/perdiem-pay-rates`);

        return this.http.get<any>(url);
    }

    searchContractRequests(args: any): Observable<ContractRequestsGridResponse> {
        let url = `${this.coreApiPath}/recruiting/contract-requests`;
        if (args.filter) {
            url += getQueryString(args.filter);
        }
        if (args.sort && args.sort.field && args.sort.field.length) {
            url = appendQuery(url, `sortField=${args.sort.field}&sortType=${args.sort.dir || 'asc'}`);
        }

        const path = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);

        return this.getPagedNew(path);
    }

    getCandidateSources() {
        return this.http.get<Lookup[]>(`${this.coreApiPath}/candidate/sources?activeOnly=true`);
    }

    getEmploymentTypes() {
        return this.http.get<Lookup[]>(`${this.coreApiPath}/candidate/employment-types`);
    }

    getExpertises() {
        return this.http.get<Expertise[]>(`${this.coreApiPath}/candidate/expertises`);
    }

    getProfessions() {
        return this.http.get<Profession[]>(`${this.coreApiPath}/candidate/professions`);
    }

    getWorkStatuses() {
        return this.http.get<Lookup[]>(`${this.coreApiPath}/candidate/work-statuses`);
    }

    getDashboardTileFollowups(recruiterId: number): Observable<DashboardTileFollowups> {
        const url = `${this.coreApiPath}/recruiting/${recruiterId}/dashboard-tile-followups`;
        return this.http.get<DashboardTileFollowups>(url);
    }

    getRecruiterFollowupSummary(id: number, startDate: Date, endDate: Date): Observable<FollowupSummary[]> {
        const startDatefmtd = formatDate(startDate);
        const endDatefmtd = formatDate(endDate);
        const url = `${this.coreApiPath}/recruiting/${id}/${startDatefmtd}/${endDatefmtd}/followup-summary`;

        return this.http.get<FollowupSummary[]>(url);
    }

    getRecruiterFollowupsByDateRange(
        id: number,
        selectedFollowupId: number,
        startDate: Date,
        endDate: Date
    ): Observable<Followup[]> {
        const startDatefmtd = formatDate(startDate);
        const endDatefmtd = formatDate(endDate);
        const url = `${this.coreApiPath}/recruiting/${id}/${startDatefmtd}/${endDatefmtd}/followups?selectedFollowupId=${selectedFollowupId}`;
        return this.http.get<Followup[]>(url);
    }

    getRecruiterFollowupForCandidate(recruiterId: number, candidateId: number): Observable<FollowupView> {
        const url = `${this.coreApiPath}/recruiting/followups/${recruiterId}/${candidateId}`;
        return this.http.get<FollowupView>(url);
    }

    getOpenFollowupsCountByDateRange(recruiterId: number, startDate: Date, endDate: Date): Observable<number> {
        const startDatefmtd = formatDate(startDate);
        const endDatefmtd = formatDate(endDate);
        const url = `${this.coreApiPath}/recruiting/${recruiterId}/open-followups-count/${startDatefmtd}/${endDatefmtd}`;
        return this.http.get<number>(url);
    }

    getCandidateOffersRecruiterReviewCount(recruiterId: number) {
        const url = `${this.coreApiPath}/recruiting/${recruiterId}/candidate-offers-recruiter-review-count`;
        return this.http.get<number>(url);
    }

    getCandidateFollowups(
        recruiterId: number,
        args: FetchArgs
    ): Observable<PagingTokenFixed<CandidateFollowupsGridItem[]>> {
        let url = `${this.coreApiPath}/recruiting/${recruiterId}/candidate-followups`;
        if (args.filter) {
            for (const filterName in args.filter) {
                const val = args.filter[filterName];
                if (Array.isArray(val)) {
                    url = appendQuery(url, arrayToQuery(`filter.${filterName}`, val as any[]));
                } else if (val instanceof Date) {
                    url = appendQuery(url, `filter.${filterName}=${formatDate(val)}`);
                } else if (val) {
                    url = appendQuery(url, `filter.${filterName}=${val}`);
                }
            }
        }

        if (args.sort && args.sort.field.length) {
            url = appendQuery(
                url,
                `sort.field=${args.sort.field}&sort.type=${args.sort.dir === 'asc' ? 'asc' : 'desc'}`
            );
        }

        url = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
        return this.http.get<PagingTokenFixed<CandidateFollowupsGridItem[]>>(url);
    }

    getCandidateNotes(candidateId: number): Observable<CandidateNotes> {
        const url = `${this.coreApiPath}/Recruiting/candidates/${candidateId}/notes`;
        return this.http.get<CandidateNotes>(url);
    }

    updateRecruiterResources(followup: FollowupView): Observable<FollowupView> {
        const url = `${this.coreApiPath}/Recruiting/followup`;
        return this.http.post<FollowupView>(url, followup);
    }

    upsertCandidateFollowups(followups: Followup[]): Observable<boolean> {
        const url = `${this.coreApiPath}/Recruiting/followups`;
        return this.http.post<boolean>(url, followups);
    }

    updateFollowupsStatus(followupsStatus: FollowupsStatus): Observable<number> {
        const url = `${this.coreApiPath}/Recruiting/followups-status`;
        return this.http.put<number>(url, followupsStatus);
    }

    updateFollowupsDate(followupsDate: FollowupsDate): Observable<number> {
        const url = `${this.coreApiPath}/Recruiting/followups-date`;
        return this.http.put<number>(url, followupsDate);
    }

    getFollowupTimeEntries(): Observable<FollowupTime[]> {
        const url = `${this.coreApiPath}/lookup/followup-time-codes`;
        return this.http.get<FollowupTime[]>(url);
    }

    getRelocationStatesLookup(): Observable<RelocationState[]> {
        const url = `${this.coreApiPath}/candidate/relocationstates`;
        return this.http.get<RelocationState[]>(url);
    }

    unrecommendJobs(candidateId: number, jobsIds: any[]) {
        const url = `${this.coreApiPath}/candidate/${candidateId}/recommended-jobs`;
        return this.http.put<any>(url, jobsIds, { observe: 'response' });
    }

    isEmailUsed(email: any) {
        const url = `${this.coreApiPath}/Account/emailAvailable?email=${email}`;
        return this.http.get<any>(url, { observe: 'response' });
    }

    isEmailUnique(email: any, candidateId: number) {
        const url = `${this.coreApiPath}/Account/email/validate?email=${encodeURIComponent(email)}&candidateId=${encodeURIComponent(candidateId)}`;
        return this.http.get<any>(url, { observe: 'response' });
    }

    addCandidate(restApiParams: any) {
        const url = `${this.coreApiPath}/Account/Register`;
        return this.http.post<any>(url, restApiParams, { observe: 'response' });
    }

    addNewCandidate(restApiParams: any) {
        const url = `${this.coreApiPath}/Account/Register-new`;
        return this.http.post<any>(url, restApiParams, { observe: 'response' });
    }

    getWorkHistoryItems(candidateId: number): Observable<WorkHistory[]> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/workHistory`;
        return this.http.get<WorkHistory[]>(url);
    }

    getQualifications(candidateId: number, isSubcontractor = false): Observable<Qualifications> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/qualifications?isSubcontractor=${isSubcontractor}`;
        return this.http.get<Qualifications>(url);
    }

    getQualificationsLookups(isSubcontractor = false): Observable<QualificationsLookups> {
        const url = `${this.coreApiPath}/candidate/qualifications/lookups?isSubcontractor=${isSubcontractor}`;
        return this.http.get<QualificationsLookups>(url);
    }

    saveQualifications(
        candidateId: number,
        formData: QualificationsEditForm,
        isSubcontractor = false
    ): Observable<Qualifications> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/qualifications?isSubcontractor=${isSubcontractor}`;
        return this.http.post<Qualifications>(url, formData);
    }

    getPersonalProfessionalInfo(candidateId: number, isSubContractor = false): Observable<PersonalProfessionalInfo> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/profile?isSubContractor=${isSubContractor}`;
        return this.http.get<PersonalProfessionalInfo>(url);
    }

    getDoNotProspect(candidateId: number): Observable<boolean> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/do-not-prospect`;
        return this.http.get<boolean>(url);
    }

    addToFavorites(candidateId: number, favoriteStatus: boolean) {
        const url = `${this.coreApiPath}/recruiting/favorite-candidates`;
        return this.http.post(url, { candidateId, favoriteStatus });
    }

    getPersonalProfessionalInfoLookups(): Observable<PersonalProfessionalInfoLookups> {
        const url = `${this.coreApiPath}/candidate/profile/lookups`;
        return this.http.get<PersonalProfessionalInfoLookups>(url);
    }

    getAlternateNameLookup(): Observable<AlternateNameType[]> {
        const url = `${this.coreApiPath}/candidate/profile/alternate-name-lookup`;
        return this.http.get<AlternateNameType[]>(url);
    }

    savePersonalProfessionalInfo(candidateId: number, formData: any): Observable<HttpResponse<any>> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/profile`;
        return this.http.post<any>(url, formData, { observe: 'response' });
    }

    savePersonalProfessionalInfoTyped(
        candidateId: number,
        formData: PersonalProfessionalInfoEditRequest
    ): Observable<HttpResponse<any>> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/profile`;
        return this.http.post<any>(url, formData, { observe: 'response' });
    }

    emitReloadEvent(): void {
        this._reloadEvent$.next();
    }

    getReloadEvent(): Observable<void> {
        return this._reloadEvent$.asObservable();
    }

    emitRequestEditMarketingPreferencesEvent(): void {
        this._requestEditMarketingPreferencesEvent$.next();
    }

    getRequestEditMarketingPreferencesEvent(): Observable<void> {
        return this._requestEditMarketingPreferencesEvent$.asObservable();
    }

    getCandidateExpertise(candidateId: number): Observable<CandidateExpertise[]> {
        const url = `${this.coreApiPath}/Candidate/${candidateId}/expertise`;
        return this.http.get<CandidateExpertise[]>(url);
    }

    saveCandidateExpertise(candidateId: number, expertise: CandidateExpertise[]): Observable<CandidateExpertise[]> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/expertise`;
        return this.http.post<CandidateExpertise[]>(url, expertise);
    }

    getCandidateResume(candidateId: number) {
        const url = `${this.coreApiPath}/candidate/${candidateId}/resume`;
        return this.http.get<any>(url, { observe: 'response' });
    }

    saveCandidateResume(candidateId: number, text: string) {
        const url = `${this.coreApiPath}/candidate/${candidateId}/resume`;
        const headers = new HttpHeaders().set('Content-Type', 'application/json');
        const data = `"${text}"`;
        return this.http.put(url, data, { headers });
    }

    getCandidateResumeDocument(candidateId: number): Observable<any> {
        const url = `${this.coreApiPath}/candidate/${candidateId}/resume/download`;
        return this.http.get<any>(url, { responseType: 'blob' as 'json' });
    }

    downloadCandidateResumeDocument(candidateId: number, docName: string): Observable<any> {
        const url = `${this.coreApiPath}/acs/download`;
        const params = new HttpParams().append('candidateId', candidateId).append('docName', docName);
        return this.http.get<any>(url, { params: params, responseType: 'blob' as 'json' });
    }

    getReasonsForLeaving() {
        const url = `${this.coreApiPath}/candidate/workHistory/reasonsForLeaving`;
        return this.http.get<any>(url, { observe: 'response' });
    }

    addEditWorkHistoryEntry(candidateId: number | null, formData: any, isEditing: boolean) {
        const url = `${this.coreApiPath}/candidate/${candidateId}/workHistory`;
        if (isEditing) {
            return this.http.put<any>(url, formData, { observe: 'response' });
        } else {
            return this.http.post<any>(url, formData, { observe: 'response' });
        }
    }

    getWorkHistoryEntry(candidateId: number | null, workhistoryId: number | null) {
        const url = `${this.coreApiPath}/candidate/${candidateId}/workHistory/${workhistoryId}`;
        return this.http.get<any>(url);
    }

    saveWorkHistoryHideFromApp(
        candidateId: number,
        workHistoryId: number,
        hideFromApp: boolean,
        infoType: WorkHistoryInfoType
    ) {
        const url = `${this.coreApiPath}/candidate/${candidateId}/workHistory/${workHistoryId}?hideFromApp=${hideFromApp}&infoType=${infoType}`;
        return this.http.put<any>(url, { observe: 'response' });
    }

    deleteWorkHistoryEntry(candidateId: number | null, workHistoryId: number | null) {
        const url = `${this.coreApiPath}/candidate/${candidateId}/workHistory/${workHistoryId}`;
        return this.http.delete<any>(url, { observe: 'response' });
    }

    deletePositionsHeldEntry(positionId: number) {
        const url = `${this.coreApiPath}/candidate/PositionsHeld/${positionId}`;
        return this.http.delete<void>(url, { observe: 'response' });
    }

    saveGapOverlapExplanation(candidateId: number, workHistoryId: number, gapEntry: any) {
        const url = `${this.coreApiPath}/candidate/${candidateId}/workHistory/${workHistoryId}/gaps-overlaps`;
        return this.http.put<any>(url, gapEntry, { observe: 'response' });
    }

    saveGapOverlap(candidateId: number, workHistoryId: number, gapEntry: GapOverlapEntry) {
        const url = `${this.coreApiPath}/candidate/${candidateId}/workHistory/${workHistoryId}/gap-overlap`;
        return this.http.put<WorkHistoryGapOverlapDetails>(url, gapEntry, { observe: 'response' });
    }

    getJobPreferences(candidateId: number) {
        const url = `${this.coreApiPath}/candidate/${candidateId}/job-preferences`;
        return this.http.get<JobPreferences>(url);
    }

    getJobPreferencesLookups() {
        const url = `${this.coreApiPath}/candidate/job-preferences-lookups`;
        return this.http.get<any>(url, { observe: 'response' });
    }

    updateJobPreferences(candidateId: number, jobPreferences: any) {
        const url = `${this.coreApiPath}/candidate/${candidateId}/job-preferences`;
        return this.http.put<any>(url, jobPreferences, { observe: 'response' });
    }

    getEducationInfoLookup(): Observable<EducationLookupInfo> {
        const url = `${this.coreApiPath}/candidate/educationLookups`;
        return this.http.get<EducationLookupInfo>(url);
    }

    getCandidateEducations(candidateUserId: number): Observable<CandidateEducation[]> {
        const url = `${this.coreApiPath}/candidate/${candidateUserId}/candidateEducations`;
        return this.http.get<CandidateEducation[]>(url);
    }

    createCandidateEducation(candidateUserId: number, candidateEducationDto: AddCandidateEducation): Observable<void> {
        const url = `${this.coreApiPath}/candidate/${candidateUserId}/candidateEducations`;
        return this.http.post<void>(url, candidateEducationDto);
    }

    updateCandidateEducation(
        candidateUserId: number,
        candidateEducationDto: UpdateCandidateEducationDto
    ): Observable<void> {
        const url = `${this.coreApiPath}/candidate/${candidateUserId}/candidateEducations`;
        return this.http.put<void>(url, candidateEducationDto);
    }

    deleteCandidateEducation(candidateUserId: number, candidateEducationId: number): Observable<void> {
        const url = `${this.coreApiPath}/candidate/${candidateUserId}/candidateEducations/${candidateEducationId}`;
        return this.http.delete<void>(url);
    }

    getMarketingPreferences(candidateId: number) {
        const url = `${this.coreApiPath}/candidates/${candidateId}/notification-settings`;
        return this.http.get<MarketingPreferencesEntity>(url);
    }

    updateJobDesires(recruiterId: number, candidateId: number, jobDesiresText: string) {
        const url = `${this.coreApiPath}/Recruiting/job-desires`;
        return this.http.post<any>(url, {
            recruiterId,
            candidateId,
            jobDesiresText
        });
    }

    updateTravelNeeds(recruiterId: number, candidateId: number, travelNeedsText: string) {
        const url = `${this.coreApiPath}/Recruiting/travel-needs`;
        return this.http.post<any>(url, {
            recruiterId,
            candidateId,
            travelNeedsText
        });
    }

    updateMarketingPreferences(candidateId: number, preferences: MarketingPreferencesEntity) {
        const url = `${this.coreApiPath}/candidates/${candidateId}/notification-settings`;
        return this.http.post<any>(url, preferences);
    }

    getTextingMessages(candidateId: number) {
        const url = `${this.coreApiPath}/candidates/${candidateId}/notifications`;
        return this.http.get<TextMessage[]>(url);
    }

    getReferenceCheck(candidateId: string, referenceCheckId: string): Observable<CandidateReferenceCheckDetails> {
        const url = `${this.coreApiPath}/Recruiting/candidates/${candidateId}/reference-check/${referenceCheckId}`;
        return this.http.get<CandidateReferenceCheckDetails>(url);
    }

    rejectContract(contractData: RejectContract): Observable<boolean> {
        return this.http.post<boolean>(`${this.coreApiPath}/Recruiting/reject-contract`, contractData);
    }

    getLicensureDetails(stateId) {
        const url = `${this.coreApiPath}/state-licensure/${stateId}/details`;
        return this.http.get<StateLicensureDetails[]>(url);
    }

    getLicensureFile(stateId, fileId) {
        const url = `${this.coreApiPath}/state-licensure/${stateId}/files/${fileId}`;
        return this.http.get(url, { observe: 'response', responseType: 'blob' });
    }

    getLicensureFiles(stateId) {
        const url = `${this.coreApiPath}/state-licensure/${stateId}/files`;
        return this.http.get<StateLicensureFile>(url);
    }

    downloadReferenceCheckPdf(
        candidateId: number | undefined,
        referenceCheckId: number | undefined,
        fileName: string
    ): void {
        const url = `${this.coreApiPath}/candidates/${candidateId}/reference-check-pdf/${referenceCheckId}`;
        const doc = this.getFile(url);
        this.downloadFile(doc, fileName);
    }

    isSelfApplyApplication(candidateId: number, jobId: number): Observable<boolean> {
        return this.http.get<boolean>(`${this.coreApiPath}/SelfApplyJob/check-candidate-application`, {
            params: new HttpParams().set('candidateId', candidateId).set('jobId', jobId)
        });
    }

    protected getPaged<T>(pageSize: number, skip: number, url: string): Observable<PagingToken<T[]>> {
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this.http.get<PageResponse>(path);
    }

    protected getJobGridPaged(pageSize: number, skip: number, url: string): Observable<JobGridResponse> {
        const path = appendQuery(url, `pageSize=${pageSize}`, `skip=${skip}`);
        return this.http.get<JobGridItem[]>(path, { observe: 'response' }).pipe(
            map((resp) => {
                const totalCount = Number.parseInt(resp.body.length.toString(), 10);
                const totalOpenPositionsCount = Number.parseInt(resp.body.length.toString(), 10);
                const totalFilteredOpenPositionsCount = Number.parseInt(resp.body.length.toString(), 10);
                return {
                    total: totalCount,
                    data: resp.body,
                    totalOpenPositions: totalOpenPositionsCount,
                    totalFilteredOpenPositions: totalFilteredOpenPositionsCount
                };
            })
        );
    }

    private generateUrl(args: FetchArgs, url: string) {
        if (args.filter) {
            for (const filterName in args.filter) {
                const val = encodeURIComponent(args.filter[filterName]);

                if (Array.isArray(val)) {
                    url = appendQuery(url, arrayToQuery(`filter.${filterName}`, val as any[]));
                } else if (val) {
                    url = appendQuery(url, `filter.${filterName}=${val}`);
                }
            }
        }

        if (args.sort && args.sort.field.length) {
            url = appendQuery(
                url,
                `sort.field=${args.sort.field}&sort.type=${args.sort.dir === 'asc' ? 'asc' : 'desc'}`
            );
        }

        url = appendQuery(url, `pageSize=${args.pageSize}`, `skip=${args.skip}`);
        return url;
    }

    private getPagedNew(url: string): Observable<ContractRequestsGridResponse> {
        return this.http.get<ContractRequestsGridResponse>(url, { observe: 'response' }).pipe(
            map((resp) => {
                return {
                    total: resp.body.total,
                    data: resp.body.data
                };
            })
        );
    }

    private getFile(url: string): Observable<{ file: Blob; url: string }> {
        return this.http.get(url, { responseType: 'blob' }).pipe(
            map((file) => {
                const url = this._window.window.URL.createObjectURL(file);
                return { file, url };
            })
        );
    }

    private downloadFile(value: Observable<{ file: Blob; url: string }>, fileName: string): void {
        value.pipe(take(1)).subscribe((file) => {
            const downloadLink = this._window.document.createElement('a');
            downloadLink.download = fileName;
            downloadLink.href = file.url;
            downloadLink.click();
        });
    }

    /**
     * URI Encode filters that are text
     * @param values
     * @returns
     */
    private encodeFilter(values: any): any {
        if (Array.isArray(values)) {
            const tempList = [];
            values.forEach((elem) => {
                if (typeof elem === 'string') {
                    tempList.push(encodeURIComponent(elem));
                } else {
                    tempList.push(elem);
                }
            });
            return tempList;
        } else if (typeof values === 'string') {
            return encodeURIComponent(values);
        } else {
            return values;
        }
    }

    /**
     * URI DECODE filters that are text
     * @param values
     * @returns
     */
    private decodeFilter(values: any): any {
        if (Array.isArray(values)) {
            const tempList = [];
            values.forEach((elem) => {
                if (typeof elem === 'string' && this.isEncoded(elem)) {
                    tempList.push(decodeURIComponent(elem));
                } else {
                    tempList.push(elem);
                }
            });
            return tempList;
        } else if (typeof values === 'string' && this.isEncoded(values)) {
            return decodeURIComponent(values);
        } else {
            return values;
        }
    }

    private isEncoded(fieldValue: string): boolean {
        fieldValue = fieldValue || '';
        return fieldValue !== decodeURIComponent(fieldValue);
    }

    // Get Candidate's recruiter's email data
    getCandidate(candidateId: number): Observable<CandidateGridItem> {
        return this.http.get<CandidateGridItem>(`${this.coreApiPath}/candidates/${candidateId}`);
    }

    getRecruiterQualifiedCandidates(jobId: number): Observable<RecruiterQualifiedCandidate[]> {
        return this.http.get<RecruiterQualifiedCandidate[]>(
            `${this.coreApiPath}/job-qualifications/qualified-candidates-recruiter/${jobId}`
        );
    }
}
